import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@performyard/py-components";
import {
	GetMeetingsIntegrationsAvailableProvidersResponse,
	GetMeetingsIntegrationsSettingsResponse,
	GetUserCalendarIntegrationStatusResponse,
	GetUserVideoConferencingStatusResponse,
	InitiateCalendarIntegrationOAuthFlowResponse,
	InitiateVideoConferencingOAuthFlowResponse,
	PatchMeetingsIntegrationsSettingsRequest,
	SaveSelectedCalendarRequest,
	VideoConferencingProviderEnum,
} from "@performyard/service-contracts";

import {
	meetingsIntegrationsAvailableProvidersQuery,
	meetingsIntegrationsSettingsQuery,
	userCalendarIntegrationStatusQuery,
	userCalendarsQuery,
	userVideoConferencingStatusQuery,
} from "../../queries";
import {
	disconnectCalendarIntegration,
	disconnectVideoConferencingIntegration,
	getCalendarIntegrationOAuthUrl,
	getVideoConferencingOAuthUrl,
	saveSelectedCalendar,
	updateMeetingsIntegrationsSettings,
} from "../../services/defs/internal-api";
import { PYUseQueryOptions } from "../StandardQueryArguments";

export const useMeetingsIntegrationsSettings = (
	options?: PYUseQueryOptions<GetMeetingsIntegrationsSettingsResponse>
) => {
	const query = useQuery({
		...meetingsIntegrationsSettingsQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

export const useUpdateMeetingsIntegrationsSettings = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: PatchMeetingsIntegrationsSettingsRequest) => updateMeetingsIntegrationsSettings(data),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["meetings-integrations-settings"] });
		},
	});
};

export const useCalendarIntegrationOAuthUrl = ({ provider }: { provider: "google" | "outlook" }) => {
	const {
		mutateAsync,
		isPending: isLoading,
		error,
	} = useMutation<InitiateCalendarIntegrationOAuthFlowResponse>({
		mutationFn: () => getCalendarIntegrationOAuthUrl({ provider }),
		onSuccess: (data) => {
			// When we get the auth URL, redirect the user to it
			if (data.authUrl) {
				window.location.href = data.authUrl;
			}
		},
	});

	return {
		initiateCalendarIntegrationAuth: mutateAsync,
		isLoading,
		error,
	};
};

export const useUserCalendarIntegrationStatus = (
	options?: PYUseQueryOptions<GetUserCalendarIntegrationStatusResponse>
) => {
	const query = useQuery({
		...userCalendarIntegrationStatusQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
	};
};

export const useDisconnectCalendarIntegration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			return disconnectCalendarIntegration();
		},
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful disconnect
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			toast.success("Calendar Successfully Disconnected");
		},
		onError: (error) => {
			console.error("Error Disconnecting Calendar:", error);
		},
		// Global error handler surfaces the backend message, falling back to this when none is sent.
		meta: { errorMessage: "Failed to Disconnect Calendar" },
	});
};

/**
 * Unified hook to get user calendars from any provider
 */
export const useUserCalendars = ({
	provider,
	enabled = true,
}: {
	provider: "google" | "outlook";
	enabled?: boolean;
}) => {
	const query = useQuery({
		...userCalendarsQuery(provider),
		enabled,
	});

	const data = query?.error ? { calendars: [] } : query?.data;

	return {
		data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
		error: query?.error,
	};
};

export const useSaveSelectedCalendar = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: SaveSelectedCalendarRequest) => saveSelectedCalendar(data),
		onSuccess: async () => {
			// Invalidate and refetch user calendar status after successful save
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
			toast.success("Calendar Successfully Changed");
		},
		onError: async (error) => {
			console.error("Error Changing Calendar:", error);
			// Saving a calendar runs through ensureValidAccessToken, which can clear a dead connection
			// server-side; resync status so the card/modal reflect the disconnect.
			await queryClient.invalidateQueries({ queryKey: userCalendarIntegrationStatusQuery().queryKey });
		},
		// Global error handler surfaces the backend message, falling back to this when none is sent.
		meta: { errorMessage: "Failed to Change Calendar" },
	});
};

// ---------- Video Conferencing ----------

/** Fetches the server-driven list of video-conferencing providers. */
export const useMeetingsIntegrationsAvailableProviders = (
	options?: PYUseQueryOptions<GetMeetingsIntegrationsAvailableProvidersResponse>
) => {
	const query = useQuery({
		...meetingsIntegrationsAvailableProvidersQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
	};
};

/** The user's current video-conferencing connection, or null if not connected. */
export const useUserVideoConferencingStatus = (options?: PYUseQueryOptions<GetUserVideoConferencingStatusResponse>) => {
	const query = useQuery({
		...userVideoConferencingStatusQuery(),
		...options,
	});

	return {
		data: query?.data,
		isLoading: query?.isLoading,
		refetch: query?.refetch,
	};
};

/** Initiates a same-tab OAuth flow; on success navigates to the returned authUrl. */
export const useVideoConferencingOAuthUrl = ({ provider }: { provider: VideoConferencingProviderEnum }) => {
	const {
		mutateAsync,
		isPending: isLoading,
		error,
	} = useMutation<InitiateVideoConferencingOAuthFlowResponse>({
		mutationFn: () => getVideoConferencingOAuthUrl({ provider }),
		onSuccess: (data) => {
			if (data.authUrl) {
				window.location.href = data.authUrl;
			}
		},
	});

	return {
		initiateVideoConferencingAuth: mutateAsync,
		isLoading,
		error,
	};
};

export const useDisconnectVideoConferencingIntegration = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ provider }: { provider: VideoConferencingProviderEnum }) => {
			return disconnectVideoConferencingIntegration({ provider });
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["user-video-conferencing-status"] });
			toast.success("Successfully Disconnected");
		},
		onError: (error) => {
			console.error("Error disconnecting video conferencing integration:", error);
			toast.error("Failed to disconnect");
		},
	});
};
